import Alert from "view/components/Alert";
import React from "react";
import type {ModifyTagArgs} from "view/components/TaskDrawer/TaskSettings";
import useStyles from "./styles";

// Minimal row shape so both show-tags and product-tags share the picker.
export type TagDefinitionLite = {id: string; name: string};

// Local picker row. `id` is absent until persisted; `isSelected` /
// `isEdit` are UI-only and never round-trip to the server. `localKey` is the
// client-side handle a not-yet-created tag is addressed by — staged
// translations hang off it until the tag is persisted and gets a real id.
export type TagListItem = {
  id?: string;
  localKey?: string;
  name: string;
  isSelected: boolean;
  isEdit?: boolean;
};

export type NewTag = {text: string; isOpen: boolean; localKey?: string};

export const LOCAL_TAG_KEY_PREFIX = "tag:local:";

/**
 * Localisation target key for a picker row: persisted tags are addressed by
 * their id, unsaved ones by their `localKey` (see `LOCAL_TAG_KEY_PREFIX`).
 */
export const toTagTargetKey = ({
  id,
  localKey,
}: Pick<TagListItem, "id" | "localKey">) =>
  id ? `tag:${id}` : `${LOCAL_TAG_KEY_PREFIX}${localKey}`;

export type ErrorStates =
  | "none"
  | "already-exist"
  | "disable-submit"
  | "something-else";

export const isError = (error: ErrorStates) => error !== "none";

export function AlertSameTag() {
  const classes = useStyles();
  return (
    <Alert severity="error">
      <span className={classes.alertText}>
        This tag name already exists. <br />
        Please enter a unique tag name.
      </span>
    </Alert>
  );
}

export function WarningMaxCount() {
  const classes = useStyles();
  return (
    <span className={classes.warningText}>
      Character limits reached (max.22)
    </span>
  );
}

type FindTagsToModifyArgs = {
  addedTagIds: string[];
  taskTags: TagDefinitionLite[];
  tagsState: TagListItem[];
};

export const findTagsToModify = ({
  addedTagIds,
  tagsState,
  taskTags,
}: FindTagsToModifyArgs): ModifyTagArgs[] => {
  const addedAndEditedTags = tagsState.reduce<ModifyTagArgs[]>((acc, tag) => {
    if (!tag.id) {
      return acc.concat({
        type: "add",
        isNew: true,
        text: tag.name,
        localKey: tag.localKey,
        shouldAddToEntity: tag.isSelected,
      });
    }

    const modifies: ModifyTagArgs[] = [];

    const isEdited = !!taskTags.find(
      (taskTag) => taskTag.id === tag.id && taskTag.name !== tag.name
    );
    if (isEdited) {
      modifies.push({type: "edit", id: tag.id, text: tag.name});
    }

    const isTagSelected = !addedTagIds.includes(tag.id) && tag.isSelected;
    if (isTagSelected) {
      modifies.push({type: "add", isNew: false, id: tag.id});
    }

    const isTagUnselected = addedTagIds.includes(tag.id) && !tag.isSelected;
    if (isTagUnselected) {
      modifies.push({type: "remove", isDBRemoval: false, id: tag.id});
    }

    return acc.concat(modifies);
  }, []);

  const removedTags: ModifyTagArgs[] = taskTags.reduce<ModifyTagArgs[]>(
    (acc, cur) => {
      const isTagExist = tagsState.find((tag) => tag.id === cur.id);
      if (isTagExist) {
        return acc;
      }
      return acc.concat({type: "remove", id: cur.id, isDBRemoval: true});
    },
    []
  );

  return addedAndEditedTags.concat(removedTags);
};
